import React from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Receipt, ShieldCheck, FileText } from 'lucide-react';
import { NumberInput } from './NumberInput';
import { formatIDR2 } from '../lib/format';

const WHT_TYPES = ['PPh', 'PPh 21', 'PPh 23', 'PPh 4(2)', 'Other'];
const WHT_BASES = [
  { value: 'dpp', label: 'DPP (setelah diskon)' },
  { value: 'gross_invoice', label: 'Grand Total (DPP + PPN)' },
  { value: 'custom', label: 'Custom (input manual)' },
];

/**
 * Pajak & Potongan section for invoice form — e-Faktur DPP Nilai Lain style.
 *
 * Tax summary shows: DPP / DPP Nilai Lain / PPN / Grand Total
 * PPh (Potongan) is rendered as a SEPARATE block below — it never reduces
 * the Grand Total, only the Jumlah yang Harus Dibayar Client (cash).
 */
export const TaxSection = ({ tax, onChange, totals, settings = {} }) => {
  const defaultWhtRate = Number(settings.default_withholding_tax_rate) || 2;
  const defaultWhtType = settings.default_withholding_tax_type || 'PPh';
  const defaultWhtBase = settings.default_withholding_tax_base || 'dpp';
  const defaultPphLabel = settings.default_withholding_tax_label || `${defaultWhtType} ${defaultWhtRate}% dari DPP`;

  const set = (patch) => onChange({ ...tax, ...patch });

  return (
    <div className="rounded-xl border border-border bg-card/40 p-4 space-y-4" data-testid="tax-section">
      <div className="flex items-center gap-2">
        <Receipt className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-heading font-semibold">Pajak & Potongan</h3>
        <span className="text-[10px] text-muted-foreground ml-auto">Opsional per invoice</span>
      </div>

      {/* PPN toggle (e-Faktur DPP Nilai Lain) */}
      <div className={`rounded-lg border p-3 transition-colors ${tax.include_ppn ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Switch
              checked={!!tax.include_ppn}
              onCheckedChange={(v) => set({ include_ppn: v })}
              data-testid="tax-ppn-toggle"
            />
            <div className="min-w-0">
              <Label className="text-sm font-medium cursor-pointer">Tambahkan PPN</Label>
              <div className="text-[11px] text-muted-foreground">
                Metode e-Faktur (DPP Nilai Lain) — PPN dihitung otomatis sesuai aturan PKP
              </div>
            </div>
          </div>
        </div>
        {tax.include_ppn && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-border">
            <SummaryCell label="DPP" value={formatIDR2(totals?.dpp_amount || 0)} testid="tax-dpp-display" />
            <SummaryCell label="DPP Nilai Lain" value={formatIDR2(totals?.dpp_nilai_lain_amount || 0)} testid="tax-dpp-nilai-lain-display" />
            <SummaryCell label="PPN" value={formatIDR2(totals?.ppn_amount || 0)} accent="primary" testid="tax-ppn-amount-display" />
            <SummaryCell label="Grand Total" value={formatIDR2(totals?.invoice_gross_amount || 0)} accent="primary" bold testid="tax-grand-total-display" />
          </div>
        )}
      </div>

      {/* PPh / Withholding toggle */}
      <div className={`rounded-lg border p-3 transition-colors ${tax.include_withholding_tax ? 'border-amber-400/40 bg-amber-400/5' : 'border-border'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Switch
              checked={!!tax.include_withholding_tax}
              onCheckedChange={(v) => set({
                include_withholding_tax: v,
                withholding_tax_rate: tax.withholding_tax_rate ?? defaultWhtRate,
                withholding_tax_type: tax.withholding_tax_type ?? defaultWhtType,
                withholding_tax_base: tax.withholding_tax_base ?? defaultWhtBase,
              })}
              data-testid="tax-pph-toggle"
            />
            <div className="min-w-0">
              <Label className="text-sm font-medium cursor-pointer">Tambahkan {defaultPphLabel}</Label>
              <div className="text-[11px] text-muted-foreground">Pajak yang dipotong klien — tidak mengurangi Grand Total, hanya mengurangi Jumlah Dibayar</div>
            </div>
          </div>
        </div>

        {tax.include_withholding_tax && (
          <div className="space-y-3 mt-3 pt-3 border-t border-amber-400/20">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px]">Jenis PPh</Label>
                <Select
                  value={tax.withholding_tax_type || defaultWhtType}
                  onValueChange={(v) => set({ withholding_tax_type: v })}
                >
                  <SelectTrigger className="h-11 md:h-9 text-base md:text-sm" data-testid="tax-pph-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WHT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px]">Rate PPh (%)</Label>
                <NumberInput
                  value={tax.withholding_tax_rate ?? defaultWhtRate}
                  onChange={(v) => set({ withholding_tax_rate: v })}
                  className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
                  testid="tax-pph-rate"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px]">Dasar Hitung PPh</Label>
                <Select
                  value={tax.withholding_tax_base || defaultWhtBase}
                  onValueChange={(v) => set({ withholding_tax_base: v })}
                >
                  <SelectTrigger className="h-11 md:h-9 text-base md:text-sm" data-testid="tax-pph-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WHT_BASES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {tax.withholding_tax_base === 'custom' && (
                <div className="space-y-1.5 col-span-2 sm:col-span-3">
                  <Label className="text-[11px]">Dasar Hitung Custom (Rp)</Label>
                  <NumberInput
                    value={tax.withholding_tax_custom_base ?? 0}
                    onChange={(v) => set({ withholding_tax_custom_base: v })}
                    className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
                    testid="tax-pph-custom-base"
                  />
                </div>
              )}
            </div>

            {/* Bukti Potong */}
            <div className="rounded-md border border-border bg-card/60 p-3 space-y-3">
              <div className="flex items-center gap-2 text-xs">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
                <span className="font-medium">Bukti Potong</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Status Bukti Potong</Label>
                  <Select
                    value={tax.withholding_status || 'Waiting'}
                    onValueChange={(v) => set({ withholding_status: v })}
                  >
                    <SelectTrigger className="h-11 md:h-9 text-base md:text-sm" data-testid="tax-wht-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Waiting">Waiting</SelectItem>
                      <SelectItem value="Received">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Nomor Bukti Potong</Label>
                  <Input
                    className="h-11 md:h-9 text-base md:text-sm"
                    value={tax.withholding_certificate_number || ''}
                    onChange={(e) => set({ withholding_certificate_number: e.target.value })}
                    data-testid="tax-bukti-potong-number"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Tanggal Bukti Potong</Label>
                  <Input
                    type="date"
                    className="h-11 md:h-9 text-base md:text-sm date-input-mobile"
                    value={tax.withholding_certificate_date || ''}
                    onChange={(e) => set({ withholding_certificate_date: e.target.value })}
                    data-testid="tax-bukti-potong-date"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== e-Faktur Tax Summary (DPP / DPP Nilai Lain / PPN / Grand Total) ===== */}
      <div className="rounded-lg border border-border bg-card/60 p-3" data-testid="tax-breakdown-summary">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-2 font-medium uppercase tracking-wide">
          <FileText className="h-3 w-3" />
          <span>Ringkasan Pajak (e-Faktur)</span>
        </div>
        <dl className="grid grid-cols-1 gap-y-1 text-sm">
          <Row label="Subtotal" value={formatIDR2(totals?.subtotal_amount || 0)} />
          {(totals?.final_discount_amount || 0) > 0 && (
            <Row label="Diskon Akhir" value={`-${formatIDR2(totals?.final_discount_amount || 0)}`} muted />
          )}
          <Row label="DPP" value={formatIDR2(totals?.dpp_amount || 0)} />
          {tax.include_ppn && (
            <>
              <Row label="DPP Nilai Lain" value={formatIDR2(totals?.dpp_nilai_lain_amount || 0)} muted />
              <Row label="PPN" value={formatIDR2(totals?.ppn_amount || 0)} />
            </>
          )}
          <Row
            label="Grand Total"
            value={formatIDR2(totals?.invoice_gross_amount || 0)}
            bold
            accent="primary"
          />
        </dl>
      </div>

      {/* ===== Separate "Potongan / Collection Summary" — never reduces Grand Total ===== */}
      {tax.include_withholding_tax && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3" data-testid="tax-pph-summary">
          <div className="flex items-center gap-1.5 text-[11px] text-amber-300 mb-2 font-medium uppercase tracking-wide">
            <ShieldCheck className="h-3 w-3" />
            <span>Potongan / Collection Summary</span>
          </div>
          <dl className="grid grid-cols-1 gap-y-1 text-sm">
            <Row
              label={`${tax.withholding_tax_type || 'PPh'} ${Number(totals?.withholding_tax_rate || 0)}% dari ${
                (totals?.withholding_tax_base === 'dpp' && 'DPP') ||
                (totals?.withholding_tax_base === 'gross_invoice' && 'Grand Total') ||
                'Custom'
              }`}
              value={`-${formatIDR2(totals?.withholding_tax_amount || 0)}`}
              muted
            />
            <Row
              label="Jumlah yang Harus Dibayar Client"
              value={formatIDR2(totals?.net_amount_to_collect || 0)}
              bold
              accent="emerald"
            />
            <Row label="Status Bukti Potong" value={tax.withholding_status || 'Waiting'} />
          </dl>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, bold = false, muted = false, accent }) => {
  const cls = [
    'flex items-center justify-between gap-3 py-0.5',
    bold ? 'font-semibold pt-1.5 border-t border-border mt-1' : '',
    muted ? 'text-muted-foreground' : '',
    accent === 'emerald' ? 'text-emerald-300' : '',
    accent === 'primary' ? 'text-primary' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <dt>{label}</dt>
      <dd className="font-mono-num">{value}</dd>
    </div>
  );
};

const SummaryCell = ({ label, value, accent, bold = false, testid }) => {
  const borderCls = accent === 'primary' ? 'border-primary/30 bg-primary/10' : 'border-border bg-white/3';
  const textCls = accent === 'primary' ? 'text-primary' : 'text-foreground';
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px]">{label}</Label>
      <div
        className={`h-11 md:h-9 rounded-md border ${borderCls} px-3 flex items-center justify-end font-mono-num text-sm ${textCls} ${bold ? 'font-semibold' : ''}`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
};
